<script setup lang="ts">
import type { ButtonVariants } from '../button/variants';
import { i18n } from '@legacy/js/plugin/vue-i18n';
import { computed, getCurrentInstance, ref } from 'vue';
import SphButton from '../button/';
import DialogGeneric from './internal/DialogGeneric.vue';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = withDefaults(defineProps<Props>(), {
    confirmButtonLabel: '',
    cancelButtonLabel: '',
    submitButtonLabel: '',
    disableSubmit: false,
    persistent: false,
});

const emit = defineEmits<{
    close: [event: CloseEvent]
    submit: [event: SubmitEvent]
}>();

export interface Props {
    variant: 'info' | 'confirm' | 'form'
    title: string
    confirmButtonLabel?: string
    cancelButtonLabel?: string
    submitButtonLabel?: string
    disableSubmit?: boolean
    // cannot be dismissed by clicking outside, pressing escape or closed by clicking the close button
    persistent?: boolean
}

const dialogProps = computed(() => {
    const nonDismissable = props.persistent || ['confirm', 'form'].includes(props.variant);

    return {
        title: props.title,
        noBackdropDismiss: props.persistent || nonDismissable,
        noEscapeDismiss: props.persistent || nonDismissable,
        noCloseButton: props.persistent,
        disableCloseButton: isSubmitting.value,
    };
});

export interface SubmitEvent { done: (success: boolean) => void }
export interface CloseEvent { reason: string, prevent: () => void }

const state = defineModel('state', {
    type: Boolean,
    default: false,
});

const instance = getCurrentInstance();
const isSubmitting = ref(false);

interface FooterButton { value: string, label: string, variant?: ButtonVariants['variant'], loading?: boolean, disabled?: boolean }

const footerButtons = computed<FooterButton[]>(() => {
    if (props.variant === 'info') {
        return [
            {
                value: 'close',
                label: i18n.t('Schließen'),
                variant: 'primary',
            },
        ];
    } else if (props.variant === 'confirm') {
        return [
            {
                value: 'confirm',
                label: props.confirmButtonLabel || i18n.t('OK'),
                variant: 'primary',
            },
            {
                value: 'cancel',
                label: props.cancelButtonLabel || i18n.t('Abbrechen'),
            },
        ];
    } else if (props.variant === 'form') {
        return [
            {
                value: 'submit',
                label: props.submitButtonLabel || i18n.t('Speichern'),
                variant: 'primary',
                loading: isSubmitting.value,
                disabled: isSubmitting.value || props.disableSubmit,
            },
            {
                value: 'cancel',
                label: props.cancelButtonLabel || i18n.t('Abbrechen'),
                disabled: isSubmitting.value,
            },
        ];
    }

    return [];
});

function emitClose(reason: string) {
    let prevented = false;
    emit('close', { reason, prevent: () => {
        prevented = true;
    } });
    if (!prevented) {
        state.value = false;
    }
}

function closeDialog(reason: string) {
    if (reason === 'submit') {
        const hasHandler = !!(instance?.vnode.props?.onSubmit);
        if (!hasHandler) {
            emitClose(reason);
            return;
        }
        isSubmitting.value = true;
        emit('submit', {
            done(success: boolean) {
                isSubmitting.value = false;
                if (success) {
                    emitClose(reason);
                }
            },
        });
        return;
    }
    emitClose(reason);
}
</script>

<template>
    <DialogGeneric
        v-model:state="state"
        v-bind="dialogProps"
        @dismiss="(reason: string) => emitClose(`dismiss:${reason}`)"
    >
        <template #default="slotProps">
            <slot v-bind="slotProps"></slot>
        </template>

        <template #footer>
            <SphButton
                v-for="button in footerButtons"
                :key="button.value"
                :variant="button.variant"
                :label="button.label"
                :loading="button.loading"
                :disabled="button.disabled"
                @click="closeDialog(button.value)"
            />
        </template>
    </DialogGeneric>
</template>
